/**
 * API Configuration
 * Centralized API endpoints, environment variables, and request settings
 */
import { getApiBaseUrl } from '../utils/common/environment';

// Environment Variables
export const ENV_CONFIG = {
  API_URL: getApiBaseUrl(),
  GEOCODE_API_KEY: process.env.REACT_APP_GEOCODE_API_KEY,
  IS_DEVELOPMENT: process.env.NODE_ENV === "development",
  IS_PRODUCTION: process.env.NODE_ENV === "production",
};

// API Base URLs
export const API_ENDPOINTS = {
  BASE: ENV_CONFIG.API_URL,
  EGNYTE: "https://rscminc.egnyte.com/pubapi",
};

// Common API Paths
export const API_PATHS = {
  // Core API paths
  CORE: {
    BASE_URL: "/core/getbaseurl",
    CURRENT_USER: "/core/getcurrentuser",
    JOB_NUMBERS: "/core/job_numbers",
    JOB_NUMBER_DETAIL: (id) => `/core/job_numbers/${id}`,
    ADDRESSES: "/core/addresses",
    CUSTOMERS: "/core/customers",
    SCOPES: "/core/scopes",
    SMS_UPDATE_NUMBER: "/core/sms/update-number",
    SMS_UPDATE_PERMISSION: "/core/sms/update-permission",
    AUTH_PHONE_START: "/core/auth/phone/start",
    AUTH_PHONE_COMPLETE: "/core/auth/phone/complete",
    IMPERSONATION_START: "/core/auth/impersonation/start",
    IMPERSONATION_STOP: "/core/auth/impersonation/stop",
    CHANGE_PASSWORD: "/core/change-password",
    DISMISS_PASSWORD_PROMPT: "/core/dismiss-password-prompt",
    USERS: "/core/users",
  },

  // Files API paths
  FILES: {
    HANDLE_IMAGE: "/files/handleimage",
    HANDLE_FILE: "/files/handlefile",
    JOB_FOLDER: "/files/jobfolder",
    JOB_DOCUMENT_FOLDERS: "/files/jobdocumentfolders",
  },

  // Dispatch API paths
  DISPATCH: {
    WEATHER_DATA: (date, jobAddress) =>
      `/dispatch/getweatherdata/${date}/${jobAddress}`,
    /** Inclusive ISO range `YYYY-MM-DD`; GET query params `start` & `end`. */
    DAY_DETAILS: "/dispatch/getdaydetails",
    MODEL_REFERENCE: "/dispatch/getmodelreferencedata",
    ADD_WORK_SCOPE: "/dispatch/addworkscopeaspect",
    DUPLICATE_SCOPES: "/dispatch/duplicatescopes",
    PATCH_DAY_DETAILS: (date) => `/dispatch/setdaydetails/${date}`,
    UPDATE_SCOPE_DETAILS: "/dispatch/updatedetails",
    UPDATE_WORK_SCOPE: "/dispatch/updateworkscopeaspect",
    UPDATE_DISPATCH_STAGE: "/dispatch/updatedispatchstage",
    DELETE_WORK_SCOPE: "/dispatch/deleteworkscopeaspect",
    DELETE_WORK_SCOPE_ASPECTS: "/dispatch/deleteworkscopeaspects",
    CLEAR_ASSIGNMENT_SET: "/dispatch/clearassignmentset",
    CLEAR_WORKERS: "/dispatch/clearworkers",
    PROJECT_CONCRETE_POURS: "/dispatch/getprojectconcretepours/",
  },

  // Timekeeping API paths
  TIMEKEEPING: {
    PENDING_PAYOUT_COUNT: "/timekeeping/getpendingpayouttimesheetscount",
    PAYOUT_DASHBOARD: "/timekeeping/getpayouttimesheetsdashboard",
    PROJECT_PAYOUT_HOURS: "/timekeeping/getprojectpayouthours/",
  },

  // Cost API paths
  COST: {
    CURRENT_JOB_COST: "/v1/getjobcost/current",
  },

  // T&M Log API paths
  TMLOG: {
    REPORTS: "/tmlog/reports/",
    REPORT_DETAIL: (id) => `/tmlog/reports/${id}/`,
    PRICES: "/tmlog/prices/",
    PRICE_DETAIL: (id) => `/tmlog/prices/${id}/`,
    REFERENCE_DATA: "/tmlog/reference/reference_data/",
  },

  // Safety Meeting API paths
  SAFETYMEETING: {
    GET_PDF: "/safetymeeting/get-pdf",
    TOPICS: "/safetymeeting/topics/",
  },

  // Schedule (scope blocks / timeline); base URL already includes /api
  SCHEDULE: {
    GROUPS: "/schedule/groups/",
    BLOCKS: "/schedule/blocks/",
    WEATHER: "/schedule/weather/",
  },

  // Planning sync-log API paths
  PLANNING: {
    SYNC_LOG: "/planning/sync-log/",
    DAY_DETAILS_LOCK: (date) => `/planning/day-details/${date}/lock/`,
    PATCH_WORK_SCOPE_ASPECT_WORKERS: "/planning/work-scope-aspects/workers/",
    PATCH_WORK_SCOPE_ASPECT: (id) => `/planning/work-scope-aspects/${id}/`,
  },

  // Project Homepage API paths
  PROJECTS: {
    LIST: "/projects/",
    DETAIL: (id) => `/projects/${id}/`,
  },
};

// Request Configuration Defaults
export const REQUEST_CONFIG = {
  withCredentials: true,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFTOKEN",
  timeout: 30000, // 30 seconds
};

// Sentry Configuration
export const SENTRY_CONFIG = {
  DSN: process.env.REACT_APP_SENTRY_DSN,
  environment: ENV_CONFIG.IS_DEVELOPMENT ? "development" : "production",
  sendDefaultPii: true,
  debug: ENV_CONFIG.IS_DEVELOPMENT,
  release: "frontend@1.0.0",
  replaysSessionSampleRate: ENV_CONFIG.IS_DEVELOPMENT ? 1.0 : 0,
  replaysOnErrorSampleRate: 1.0,
};
